import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Plus, Play, Trash2, Edit, Copy, MoreVertical, 
  Search, Calendar, Clock, Power, PowerOff, ChevronRight, ChevronLeft, Home, FolderOpen, FolderInput, ChevronDown, ChevronUp
} from 'lucide-react';
import { useDndMonitor } from '@dnd-kit/core';
import { workflowsApi, foldersApi, teamFoldersApi, teamsApi } from '@/lib/api';
import { formatTimestamp } from '@/utils/date-utils';
import { Button } from '@/components/ui/button';
import { DraggableWorkflowRow } from '@/components/workflow/DraggableWorkflowRow';
import { MoveToTeamFolderDialog } from '@/components/workflow/MoveToTeamFolderDialog';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { eventEmitter, EVENTS } from '@/lib/events';
import { WorkflowListSkeleton } from '@/components/ui/workflow-skeleton';
import { useSubscription } from '@/contexts/SubscriptionContext';

interface Folder {
  id: string;
  name: string;
  parentId: string | null;  // Changed to camelCase to match backend
  userId?: string | null;
  createdAt?: number;
  updatedAt?: number;
  children?: Folder[];
  workflowCount?: number;
}

interface Workflow {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;  // ISO string from backend
  updatedAt: string;  // ISO string from backend
  folderId?: string | null;
  teamFolderId?: string | null;
}

export default function WorkflowList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriptionId, loading: subscriptionLoading } = useSubscription();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedTeamFolderId, setSelectedTeamFolderId] = useState<string | null>(null);
  const [teamContext, setTeamContext] = useState<{ teamId: string; teamName: string; folderName: string; isRoot: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newWorkflow, setNewWorkflow] = useState({ name: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [runningWorkflows, setRunningWorkflows] = useState<Set<string>>(new Set());
  
  // Debug log for status filter changes
  useEffect(() => {
  }, [statusFilter]);

  // Get page from URL query params (needed early for pagination state init)
  const pageFromUrl = searchParams.get('page');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Track if we've initialized from URL (to prevent reset effect from overriding)
  const hasInitializedFromUrl = useRef(false);

  // Responsive page size based on viewport height
  const pageSize = useViewportPageSize({
    rowHeight: 62,                        // Height of each table row (measured from actual rendering)
    minPageSize: 5,                       // Lower minimum for smaller screens
    maxPageSize: 50,                      // Reasonable maximum to avoid performance issues
    bufferSpace: 45,                      // 40px header row + 5px safety margin
    loading,                              // Recalculate when loading completes
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'name' | 'status' | 'created' | 'updated'>('updated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Move to folder state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState<Set<string>>(new Set());
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  // Move to team folder state
  const [moveToTeamDialogOpen, setMoveToTeamDialogOpen] = useState(false);
  const [workflowToMoveToTeam, setWorkflowToMoveToTeam] = useState<{ id: string; name: string } | null>(null);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  
  // Get folder ID from URL query params
  const folderIdFromUrl = searchParams.get('folder');
  const teamFolderIdFromUrl = searchParams.get('teamFolder');
  const refreshParam = searchParams.get('refresh');

  // Define loadData before it's used in useDndMonitor
  const loadData = useCallback(async () => {
    try {
      // Pass folderId to backend for server-side filtering
      const folderId = teamFolderIdFromUrl || folderIdFromUrl || undefined;

      if (teamFolderIdFromUrl) {
        const [workflowsResponse, teamFolderResponse] = await Promise.all([
          workflowsApi.list({ folderId }),
          teamFoldersApi.get(teamFolderIdFromUrl),
        ]);

        const respData = workflowsResponse.data;
        const workflowsData = Array.isArray(respData?.items)
          ? respData.items
          : (Array.isArray(respData?.workflows)
            ? respData.workflows
            : (Array.isArray(respData) ? respData : []));

        setWorkflows(workflowsData);
        setFolders([]); // No personal folders in team view
        setSelectedTeamFolderId(teamFolderIdFromUrl);

        // Set team context for header
        if (teamFolderResponse.data) {
          const folder = teamFolderResponse.data;
          const teamId = folder.teamId || folder.team_id;
          const teamResponse = await teamsApi.get(teamId);
          setTeamContext({
            teamId,
            teamName: teamResponse.data?.name || 'Team',
            folderName: folder.name,
            isRoot: folder.isRoot || false,
          });
        }
      } else {
        // Load personal workflows and folders
        const [workflowsResponse, foldersResponse] = await Promise.all([
          workflowsApi.list({ folderId }),
          foldersApi.list(),
        ]);

        const respData = workflowsResponse.data;
        const workflowsData = Array.isArray(respData?.items)
          ? respData.items
          : (Array.isArray(respData?.workflows)
            ? respData.workflows
            : (Array.isArray(respData) ? respData : []));

        const foldersData = Array.isArray(foldersResponse.data?.folders)
          ? foldersResponse.data.folders
          : (Array.isArray(foldersResponse.data)
            ? foldersResponse.data
            : []);

        setWorkflows(workflowsData);
        setFolders(foldersData);
        setSelectedTeamFolderId(null);
        setTeamContext(null);
      }
    } catch (error) {
      toast.error('Failed to load data');
      // Set empty arrays on error to prevent filter errors
      setWorkflows([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [teamFolderIdFromUrl, folderIdFromUrl]);

  // Load user's teams to check if they have any teams
  useEffect(() => {
    // Wait for subscription context to be ready before making API calls
    if (subscriptionLoading || !subscriptionId) {
      return;
    }

    const loadUserTeams = async () => {
      try {
        const response = await teamsApi.list();
        setUserTeams(response.data || []);
      } catch (error) {
        console.error('Failed to load teams:', error);
        setUserTeams([]);
      }
    };
    loadUserTeams();

    // Listen for team updates
    const handleTeamsUpdated = () => {
      loadUserTeams();
    };

    eventEmitter.on(EVENTS.TEAMS_UPDATED, handleTeamsUpdated);
    eventEmitter.on(EVENTS.TEAM_CREATED, handleTeamsUpdated);
    eventEmitter.on(EVENTS.TEAM_DELETED, handleTeamsUpdated);

    return () => {
      eventEmitter.off(EVENTS.TEAMS_UPDATED, handleTeamsUpdated);
      eventEmitter.off(EVENTS.TEAM_CREATED, handleTeamsUpdated);
      eventEmitter.off(EVENTS.TEAM_DELETED, handleTeamsUpdated);
    };
  }, [subscriptionId, subscriptionLoading]);

  // Monitor drag and drop events from the parent DndContext in AppLayout
  useDndMonitor({
    onDragEnd: async (event) => {
      const { active, over } = event;

      if (!over) {
        return;
      }

      // Get the workflow being dragged
      const workflowId = active.id as string;
      const workflow = workflows.find(w => w.id === workflowId);

      if (!workflow) {
        return;
      }

      // Determine target folder from the droppable's data
      let targetFolderId: string | null = null;
      let targetTeamFolderId: string | null = null;
      let isTeamFolder = false;

      // Check if the drop target has folder data
      if (over.data.current?.type === 'team-folder') {
        // Dropping into a team folder
        isTeamFolder = true;
        targetTeamFolderId = over.data.current.teamFolderId;
      } else if (over.data.current?.type === 'folder') {
        // Dropping into a personal folder
        targetFolderId = over.data.current.folderId;
      } else if (over.id === 'root-folder') {
        // Dropping into personal root folder
        targetFolderId = null;
      } else if (over.id.toString().startsWith('folder-')) {
        // Legacy folder ID format
        targetFolderId = over.id.toString().replace('folder-', '');
      } else if (over.id.toString().startsWith('team-folder-')) {
        // Legacy team folder ID format
        isTeamFolder = true;
        targetTeamFolderId = over.id.toString().replace('team-folder-', '');
      } else if (over.id.toString().startsWith('team-root-')) {
        // Legacy team root folder ID format
        isTeamFolder = true;
        targetTeamFolderId = over.data.current?.teamFolderId;
      } else {
        return; // Dropped on invalid target
      }

      // Check if already in target folder
      const currentFolderId = workflow.folderId || (workflow as any).folder_id;
      const currentTeamFolderId = workflow.teamFolderId || (workflow as any).team_folder_id;
      
      if (isTeamFolder && currentTeamFolderId === targetTeamFolderId) {
        toast.info('Workflow is already in this folder');
        return;
      } else if (!isTeamFolder && currentFolderId === targetFolderId && !currentTeamFolderId) {
        toast.info('Workflow is already in this folder');
        return;
      }

      try {
        // Move the workflow using the appropriate API
        if (isTeamFolder && targetTeamFolderId) {
          await workflowsApi.moveToTeamFolder(workflowId, targetTeamFolderId);
          toast.success('Workflow moved to team folder successfully');
        } else {
          await workflowsApi.moveToPersonalFolder(workflowId, targetFolderId || undefined);
          const folderName = targetFolderId 
            ? folders.find(f => f.id === targetFolderId)?.name || 'folder'
            : 'My Workflows';
          toast.success(`Workflow moved to ${folderName} successfully`);
        }

        // Reload data to refresh the list
        await loadData();
        
        // Emit events to update folder counts AFTER loadData completes
        // Always emit both events since we might be moving between personal and team folders
        eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
        eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);
      } catch (error: any) {
        console.error('Failed to move workflow:', error);
        // Extract error message from API response (backend returns collection validation errors)
        const errorMessage = error?.response?.data?.error ||
                            error?.response?.data?.message ||
                            error?.message ||
                            'Failed to move workflow';
        toast.error(errorMessage);
      }
    },
  });

  // Load data on mount and when refresh parameter changes
  useEffect(() => {
    // Wait for subscription context to be ready before making API calls
    if (subscriptionLoading || !subscriptionId) {
      return;
    }
    loadData();
  }, [loadData, refreshParam, subscriptionId, subscriptionLoading]);

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }

    if (isCreating) {
      return; // Prevent multiple submissions
    }

    setIsCreating(true);
    try {
      const response = await workflowsApi.create({
        name: newWorkflow.name,
        description: newWorkflow.description,
        folderId: teamFolderIdFromUrl ? undefined : (folderIdFromUrl || undefined), // Only for personal folders
        teamFolderId: teamFolderIdFromUrl || undefined, // For team folders
        definition: {
          nodes: [],
          edges: [],
          settings: {},
        },
      });
      toast.success('Workflow created successfully');
      setCreateDialogOpen(false);
      setNewWorkflow({ name: '', description: '' });

      // Emit events to update sidebar counts
      eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
      eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);

      // Navigate with context - team folder takes priority, include page for return navigation
      const pageParam = currentPage > 1 ? `&page=${currentPage}` : '';
      let contextParam = '';
      if (teamFolderIdFromUrl) {
        contextParam = `?teamFolder=${teamFolderIdFromUrl}${pageParam}`;
      } else if (folderIdFromUrl) {
        contextParam = `?returnFolder=${folderIdFromUrl}${pageParam}`;
      } else if (currentPage > 1) {
        contextParam = `?page=${currentPage}`;
      }

      navigate(`/workflows/${response.data.id}${contextParam}`);
    } catch (error) {
      toast.error('Failed to create workflow');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteWorkflow = useCallback((id: string, name: string) => {
    setWorkflowToDelete({ id, name });
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteWorkflow = useCallback(async () => {
    if (!workflowToDelete) return;

    try {
      await workflowsApi.delete(workflowToDelete.id);
      toast.success('Workflow deleted successfully');
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);

      // Emit events to update sidebar counts
      eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
      eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);

      // Calculate filtered workflows to determine current page items count
      const filtered = workflows.filter(workflow => {
        const matchesSearch = workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             workflow.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const workflowFolderId = workflow.folderId || (workflow as any).folder_id;
        const matchesFolder = folderIdFromUrl 
          ? workflowFolderId === folderIdFromUrl 
          : workflowFolderId === null || workflowFolderId === undefined;
        const matchesStatus = statusFilter === 'all' || 
                             (statusFilter === 'active' && workflow.active) ||
                             (statusFilter === 'inactive' && !workflow.active);
        return matchesSearch && matchesFolder && matchesStatus;
      });
      
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const currentPageItemsCount = filtered.slice(startIndex, endIndex).length;
      
      // Check if we're deleting the last item on the current page
      // If so, navigate to the previous page
      if (currentPageItemsCount === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      
      loadData();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  }, [workflowToDelete, loadData, workflows, currentPage, pageSize, searchQuery, folderIdFromUrl, statusFilter]);

  const handleDuplicateWorkflow = useCallback(async (workflow: Workflow) => {
    try {
      const response = await workflowsApi.duplicate(workflow.id, `${workflow.name} (Copy)`);
      toast.success('Workflow duplicated successfully');

      // Emit events to update sidebar counts
      eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
      eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);

      loadData();
    } catch (error) {
      console.error('Failed to duplicate workflow:', error);
      toast.error('Failed to duplicate workflow');
    }
  }, [loadData]);

  const handleToggleActive = useCallback(async (workflow: Workflow) => {
    try {
      const response = await workflowsApi.toggleActive(workflow.id);
      toast.success(`Workflow ${workflow.active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      console.error('Failed to toggle workflow active state:', error);
      toast.error('Failed to update workflow');
    }
  }, [loadData]);

  const handleRunWorkflow = useCallback((workflow: Workflow) => {
    // Add to running workflows
    setRunningWorkflows(prev => new Set(prev).add(workflow.id));

    // Show a brief toast that auto-dismisses
    toast.success(`Starting workflow "${workflow.name}"`, { duration: 1500 });

    // Fire off the execution without waiting - it runs in the background
    workflowsApi.execute(workflow.id).catch((error) => {
      console.error('Workflow execution failed:', error);
      toast.error(`Failed to start workflow "${workflow.name}"`);
    });

    // Remove from running workflows after a delay
    setTimeout(() => {
      setRunningWorkflows(prev => {
        const newSet = new Set(prev);
        newSet.delete(workflow.id);
        return newSet;
      });
    }, 2000);
  }, []);

  // Move to folder handlers
  const handleOpenMoveDialog = (workflow: Workflow) => {
    // Get folder ID (handle both camelCase and snake_case)
    const workflowFolderId = workflow.folderId || (workflow as any).folder_id;
    setSelectedWorkflows(new Set([workflow.id]));
    setTargetFolderId(workflowFolderId || null);
    setMoveDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      // If we're in a team folder context, create a team sub-folder
      if (teamContext?.teamId && teamFolderIdFromUrl) {
        const response = await teamFoldersApi.createSubFolder(teamContext.teamId, {
          name: newFolderName,
          parentId: teamFolderIdFromUrl,
        });
        toast.success('Team folder created successfully');
        setNewFolderName('');
        setCreateFolderDialogOpen(false);
        
        // Emit event to refresh sidebar
        window.dispatchEvent(new CustomEvent(EVENTS.TEAM_UPDATED, { detail: { teamId: teamContext.teamId } }));
        
        // Reload data to show the new folder
        await loadData();
      } else {
        // Create a personal folder
        const response = await foldersApi.create({ name: newFolderName, parentId: null });
        toast.success('Folder created successfully');
        setNewFolderName('');
        setCreateFolderDialogOpen(false);
        await loadData();
        // Set the newly created folder as target
        setTargetFolderId(response.data.id);
      }
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleMoveWorkflows = async () => {
    if (selectedWorkflows.size === 0) {
      toast.error('No workflows selected');
      return;
    }

    // Check if target folder is different from current location
    const workflowsToMove = Array.from(selectedWorkflows);
    const workflow = workflows.find(w => w.id === workflowsToMove[0]);

    // If workflow is in a team folder, always allow moving to personal folder
    const isInTeamFolder = !!(workflow?.teamFolderId || (workflow as any)?.team_folder_id);

    // Normalize both to null for proper comparison (undefined should equal null for "no folder")
    const currentFolderId = workflow?.folderId ?? null;
    const targetFolder = targetFolderId ?? null;

    // Only block if moving within personal folders to the same folder
    if (!isInTeamFolder && currentFolderId === targetFolder) {
      toast.error('Workflow is already in this folder');
      return;
    }

    try {
      // Move each selected workflow using the dedicated move to personal folder API
      // This properly clears teamFolderId when moving from team folders
      await Promise.all(
        workflowsToMove.map(workflowId =>
          workflowsApi.moveToPersonalFolder(workflowId, targetFolderId ?? undefined)
        )
      );

      const count = selectedWorkflows.size;
      toast.success(`${count} workflow${count > 1 ? 's' : ''} moved successfully`);
      setMoveDialogOpen(false);
      setSelectedWorkflows(new Set());
      setTargetFolderId(null);

      // Emit events to update sidebar folder counts
      eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
      // Also emit team folders updated in case workflow was moved from a team folder
      if (isInTeamFolder) {
        eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);
      }

      // Reload data to refresh the list
      await loadData();
    } catch (error) {
      toast.error('Failed to move workflows');
    }
  };

  // Team folder handlers
  const handleMoveToTeamFolder = (workflow: any) => {
    setWorkflowToMoveToTeam({ id: workflow.id, name: workflow.name });
    setMoveToTeamDialogOpen(true);
  };

  const handleMoveToTeamSuccess = () => {
    loadData();
  };

  const toggleFolderExpansion = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const buildFolderTree = (parentId: string | null = null, level: number = 0): React.ReactElement[] => {
    return folders
      .filter(folder => folder.parentId === parentId)
      .map(folder => {
        const hasChildren = folders.some(f => f.parentId === folder.id);
        const isExpanded = expandedFolders.has(folder.id);
        const isSelected = targetFolderId === folder.id;
        const isCurrent = folderIdFromUrl === folder.id;

        return (
          <div key={folder.id}>
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-accent transition-colors ${
                isSelected ? 'bg-accent' : ''
              } ${isCurrent ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ paddingLeft: `${level * 20 + 12}px` }}
              onClick={() => !isCurrent && setTargetFolderId(folder.id)}
            >
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderExpansion(folder.id);
                  }}
                  className="p-0 hover:bg-transparent"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
              {!hasChildren && <div className="w-4" />}
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 text-sm">{folder.name}</span>
              {isCurrent && (
                <Badge variant="secondary" className="text-xs">Current</Badge>
              )}
              {folder.workflowCount !== undefined && !isCurrent && (
                <span className="text-xs text-muted-foreground">{folder.workflowCount}</span>
              )}
            </div>
            {isExpanded && hasChildren && buildFolderTree(folder.id, level + 1)}
          </div>
        );
      });
  };

  const filteredWorkflows = useMemo(() => {
    const filtered = workflows.filter(workflow => {
      const matchesSearch = workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           workflow.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Get folder ID from workflow (handle both camelCase and snake_case)
      const workflowFolderId = workflow.folderId || (workflow as any).folder_id;
      const workflowTeamFolderId = (workflow as any).teamFolderId;
      
      // Folder filtering is now handled server-side via folderId query param.
      // When no folder is selected, backend returns all user workflows (personal + team),
      // so we still filter client-side to show only root-level (no folder) workflows.
      let matchesFolder = true;
      if (!teamFolderIdFromUrl && !folderIdFromUrl) {
        const workflowFolderId = workflow.folderId || (workflow as any).folder_id;
        const workflowTeamFolderId = (workflow as any).teamFolderId;
        matchesFolder = (workflowFolderId === null || workflowFolderId === undefined) &&
                       (workflowTeamFolderId === null || workflowTeamFolderId === undefined);
      }

      // Status filter
      const matchesStatus = statusFilter === 'all' ||
                           (statusFilter === 'active' && workflow.active) ||
                           (statusFilter === 'inactive' && !workflow.active);

      return matchesSearch && matchesFolder && matchesStatus;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = (a.active === b.active) ? 0 : a.active ? -1 : 1;
          break;
        case 'created':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updated':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [workflows, searchQuery, folderIdFromUrl, teamFolderIdFromUrl, sortColumn, sortDirection, statusFilter]);
  
  // Paginated workflows
  const paginatedWorkflows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredWorkflows.slice(startIndex, endIndex);
  }, [filteredWorkflows, currentPage, pageSize]);
  
  // Total pages
  const totalPages = Math.ceil(filteredWorkflows.length / pageSize);
  
  // Sync page from URL when navigating back to the list
  useEffect(() => {
    if (pageFromUrl && !hasInitializedFromUrl.current) {
      const page = parseInt(pageFromUrl, 10);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
        hasInitializedFromUrl.current = true;
      }
    }
  }, [pageFromUrl]);

  // Reset to page 1 when filters or folder change (but not when initializing from URL)
  // Note: pageSize is intentionally excluded - changing page size shouldn't reset to page 1
  useEffect(() => {
    // Skip if we just initialized from URL
    if (hasInitializedFromUrl.current) {
      hasInitializedFromUrl.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchQuery, statusFilter, folderIdFromUrl, teamFolderIdFromUrl]);

  // Ensure current page is valid when page size or total items change
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Handle column sort
  const handleSort = (column: 'name' | 'status' | 'created' | 'updated') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Get current folder name for display
  const currentFolder = useMemo(() => 
    folderIdFromUrl ? folders.find(f => f.id === folderIdFromUrl) : null,
    [folderIdFromUrl, folders]
  );

  if (loading) {
    return <WorkflowListSkeleton />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 md:p-8 pt-6">
        {/* Header */}
        <div className="flex-none">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Link to="/workflows" className="hover:text-foreground transition-colors">
            <Home className="h-4 w-4" />
          </Link>
          {teamContext && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link to="/teams" className="hover:text-foreground transition-colors">
                Teams
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground font-medium">{teamContext.teamName}</span>
              {!teamContext.isRoot && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-foreground font-medium">{teamContext.folderName}</span>
                </>
              )}
            </>
          )}
          {!teamContext && currentFolder && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground font-medium">{currentFolder.name}</span>
            </>
          )}
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold">
              {teamContext 
                ? (teamContext.isRoot ? teamContext.teamName : `${teamContext.teamName} / ${teamContext.folderName}`)
                : currentFolder 
                  ? currentFolder.name 
                  : 'My Workflows'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {teamContext 
                ? 'Team workflows - accessible by all team members'
                : 'View and manage your workflows'}
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Workflow
          </Button>
        </div>

        {/* Search Bar and Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div data-table-container className="flex-1 overflow-auto">
        {filteredWorkflows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-3 mb-4">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No workflows yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Get started by creating your first workflow
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Workflow
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortColumn === 'name' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortColumn === 'status' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('created')}
                      >
                        Created
                        {sortColumn === 'created' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('updated')}
                      >
                        Updated
                        {sortColumn === 'updated' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedWorkflows.map((workflow) => {
                    // Create stable callback references for each workflow
                    const handleNavigate = () => {
                      // Pass team folder context and current page for return navigation
                      const pageParam = currentPage > 1 ? `&page=${currentPage}` : '';
                      if (teamFolderIdFromUrl) {
                        navigate(`/workflows/${workflow.id}?teamFolder=${teamFolderIdFromUrl}${pageParam}`);
                      } else if (folderIdFromUrl) {
                        navigate(`/workflows/${workflow.id}?returnFolder=${folderIdFromUrl}${pageParam}`);
                      } else {
                        navigate(`/workflows/${workflow.id}${pageParam ? `?page=${currentPage}` : ''}`);
                      }
                    };
                    const handleDelete = (id: string, name: string) => handleDeleteWorkflow(id, name);
                    
                    return (
                      <DraggableWorkflowRow
                        key={workflow.id}
                        workflow={workflow}
                        onNavigate={handleNavigate}
                        onRun={handleRunWorkflow}
                        onToggleActive={handleToggleActive}
                        onDuplicate={handleDuplicateWorkflow}
                        onMove={handleOpenMoveDialog}
                        onMoveToTeam={userTeams.length > 0 ? handleMoveToTeamFolder : undefined}
                        onDelete={handleDelete}
                        isRunning={runningWorkflows.has(workflow.id)}
                        formatTimestamp={formatTimestamp}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
      </div>
      
      {/* Pagination Footer - Fixed at bottom */}
      {filteredWorkflows.length > 0 && (
        <div data-pagination-footer className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
          <div className="text-sm text-muted-foreground text-center sm:text-left">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredWorkflows.length)} of {filteredWorkflows.length} workflows
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium whitespace-nowrap">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workflow</DialogTitle>
            <DialogDescription>
              Give your workflow a name and description to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="My Workflow"
                value={newWorkflow.name}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateWorkflow();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="What does this workflow do?"
                value={newWorkflow.description}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkflow} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWorkflowToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteWorkflow}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move to Folder Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Move Workflow to Folder</DialogTitle>
            <DialogDescription>
              Select a destination folder for {selectedWorkflows.size} workflow{selectedWorkflows.size > 1 ? 's' : ''}.
              {folderIdFromUrl && ' The current folder is disabled.'}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg">
            <div className="p-3 border-b bg-muted/50 flex items-center justify-between">
              <h4 className="font-medium text-sm">Select Folder</h4>
              <Button
                onClick={() => setCreateFolderDialogOpen(true)}
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                New Folder
              </Button>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="p-2 space-y-1">
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-accent transition-colors ${
                    targetFolderId === null ? 'bg-accent' : ''
                  }`}
                  onClick={() => setTargetFolderId(null)}
                >
                  <Home className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-sm">Root (No Folder)</span>
                </div>
                {buildFolderTree()}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMoveDialogOpen(false);
                setSelectedWorkflows(new Set());
                setTargetFolderId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMoveWorkflows}
            >
              Move {selectedWorkflows.size > 1 ? `${selectedWorkflows.size} Workflows` : 'Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialogOpen} onOpenChange={setCreateFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for your new folder to organize workflows.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                placeholder="My Folder"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateFolderDialogOpen(false);
              setNewFolderName('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Team Folder Dialog */}
      {workflowToMoveToTeam && (
        <MoveToTeamFolderDialog
          isOpen={moveToTeamDialogOpen}
          onClose={() => {
            setMoveToTeamDialogOpen(false);
            setWorkflowToMoveToTeam(null);
          }}
          workflowId={workflowToMoveToTeam.id}
          workflowName={workflowToMoveToTeam.name}
          onSuccess={handleMoveToTeamSuccess}
        />
      )}
    </div>
  );
}
